import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { 
  ArrowLeft, 
  Users, 
  User, 
  Plus, 
  Search,
  ChevronRight,
  Trash2,
  Send,
  Loader2,
  Check
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import NavBar from "../components/NavBar";

const StelxContacts = ({ user }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [addSearchQuery, setAddSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);

  // Fetch saved Stelx contacts
  const { data: contactsData, isLoading } = useQuery({
    queryKey: ["stelxContacts"],
    queryFn: async () => {
      try {
        const response = await axios.get(`${API}/contacts/stelx`);
        return response.data;
      } catch {
        return { contacts: [] };
      }
    },
  });

  // Search for users to add
  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ["userSearch", addSearchQuery],
    queryFn: async () => {
      if (!addSearchQuery || addSearchQuery.length < 2) return [];
      const response = await axios.get(`${API}/users/search?q=${encodeURIComponent(addSearchQuery)}`);
      return response.data.users || [];
    },
    enabled: addSearchQuery.length >= 2,
    staleTime: 30000,
  });

  // Add contact mutation
  const addContactMutation = useMutation({
    mutationFn: async (contactUserId) => {
      const response = await axios.post(`${API}/contacts/stelx`, { contact_user_id: contactUserId });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["stelxContacts"]);
      toast.success("Contact added!");
      setShowAddDialog(false);
      setSelectedUser(null);
      setAddSearchQuery("");
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Failed to add contact");
    }
  });

  // Delete contact mutation
  const deleteContactMutation = useMutation({
    mutationFn: async (contactId) => {
      await axios.delete(`${API}/contacts/stelx/${contactId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["stelxContacts"]);
      toast.success("Contact removed");
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Failed to remove contact");
    }
  });

  const contacts = contactsData?.contacts || [];
  
  // Filter contacts by search
  const filteredContacts = contacts.filter(contact => 
    !searchQuery || 
    contact.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contact.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contact.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectUser = (user) => {
    setSelectedUser(user);
  };

  const handleAddContact = () => {
    if (selectedUser) {
      addContactMutation.mutate(selectedUser.user_id);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="stelx-contacts-page">
      <header className="px-6 py-4 flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/explore")}
          className="text-foreground"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading font-semibold text-xl">Stelx Contacts</h1>
      </header>

      <main className="px-6 space-y-6">
        {/* Search & Add */}
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search contacts..."
              className="input-glass pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-contacts-input"
            />
          </div>
          <Button
            onClick={() => setShowAddDialog(true)}
            className="btn-primary"
            data-testid="add-contact-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add
          </Button>
        </div>

        {/* Contacts List */}
        <div className="space-y-3">
          {isLoading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto" />
              <p className="text-sm text-muted-foreground mt-2">Loading contacts...</p>
            </div>
          ) : filteredContacts.length > 0 ? (
            filteredContacts.map((contact) => (
              <div
                key={contact.contact_id}
                className="glass rounded-2xl p-4 flex items-center gap-3"
                data-testid={`contact-${contact.contact_id}`}
              >
                {/* Avatar */}
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  {contact.picture ? (
                    <img src={contact.picture} alt={contact.name} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span className="text-lg font-semibold text-primary">
                      {contact.name?.charAt(0)?.toUpperCase() || "?"}
                    </span>
                  )}
                </div>
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">{contact.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {contact.username ? `@${contact.username} • ` : ""}{contact.email}
                  </p>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    onClick={() => deleteContactMutation.mutate(contact.contact_id)}
                    data-testid={`delete-contact-${contact.contact_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    className="btn-primary"
                    onClick={() => navigate("/transfer", { state: { recipient: contact } })}
                    data-testid={`send-to-${contact.contact_id}`}
                  >
                    <Send className="w-4 h-4 mr-1" />
                    Send
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12 glass rounded-2xl">
              <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="font-medium text-foreground">No contacts yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Add Stelx users to quickly send them money
              </p>
              <Button
                onClick={() => setShowAddDialog(true)}
                className="btn-primary mt-4"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Contact
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Add Contact Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="glass-heavy border-white/[0.08] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add Stelx Contact</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Search by Email or Username</Label>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Enter email or @username"
                  className="input-glass pl-10"
                  value={addSearchQuery}
                  onChange={(e) => {
                    setAddSearchQuery(e.target.value);
                    setSelectedUser(null);
                  }}
                  data-testid="add-contact-search"
                />
                {isSearching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
                )}
              </div>
            </div>

            {/* Search Results */}
            {addSearchQuery.length >= 2 && searchResults && searchResults.length > 0 && (
              <div className="bg-white/5 rounded-xl border border-white/10 max-h-48 overflow-y-auto">
                {searchResults.map((result) => (
                  <button
                    key={result.user_id}
                    onClick={() => handleSelectUser(result)}
                    className={`w-full flex items-center gap-3 p-3 hover:bg-white/10 transition-colors text-left border-b border-white/5 last:border-0 ${
                      selectedUser?.user_id === result.user_id ? "bg-primary/20" : ""
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-primary">
                        {result.name?.charAt(0)?.toUpperCase() || "?"}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-foreground truncate">{result.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {result.username ? `@${result.username} • ` : ""}{result.email}
                      </p>
                    </div>
                    {selectedUser?.user_id === result.user_id && (
                      <Check className="w-5 h-5 text-primary flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* No results */}
            {addSearchQuery.length >= 2 && !isSearching && searchResults?.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-3">
                No Stelx users found with that email or username.
              </p>
            )}

            {/* Selected User */}
            {selectedUser && (
              <div className="bg-primary/10 rounded-xl p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="text-sm font-semibold text-primary">
                    {selectedUser.name?.charAt(0)?.toUpperCase() || "?"}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm text-foreground">{selectedUser.name}</p>
                  <p className="text-xs text-muted-foreground">{selectedUser.email}</p>
                </div>
                <Check className="w-5 h-5 text-primary" />
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowAddDialog(false);
                setSelectedUser(null);
                setAddSearchQuery("");
              }}
              className="btn-secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddContact}
              disabled={!selectedUser || addContactMutation.isPending}
              className="btn-primary"
            >
              {addContactMutation.isPending ? "Adding..." : "Add Contact"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NavBar active="wallet" />
    </div>
  );
};

export default StelxContacts;
